import { useEffect, useState, useRef } from "react";
import api, { apiErr, API } from "@/lib/api";
import { PageHeader, Loader, Button, SectionCard, EmptyState } from "@/components/PageUI";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, FileImage, Film, FileSpreadsheet, File as FileIcon, FolderOpen, Eye, EyeOff, Users } from "lucide-react";

const CATEGORIES = ["Forms", "Media", "Policies", "Schedules", "Other"];
const CHUNK = 5 * 1024 * 1024;

const fileUrl = (f, dl) => `${API}/library/${f.id}/file?auth=${f.url_token}${dl ? "&download=1" : ""}`;
const isImage = (ct) => (ct || "").startsWith("image/");
const isVideo = (ct) => (ct || "").startsWith("video/");
const humanSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const TypeIcon = ({ ct, size = 20 }) => {
  const cls = "text-primary";
  if (isImage(ct)) return <FileImage size={size} className={cls} />;
  if (isVideo(ct)) return <Film size={size} className={cls} />;
  if ((ct || "").includes("spreadsheet") || (ct || "").includes("excel") || (ct || "").includes("csv")) return <FileSpreadsheet size={size} className={cls} />;
  if ((ct || "").includes("pdf") || (ct || "").includes("word") || (ct || "").includes("text")) return <FileText size={size} className={cls} />;
  return <FileIcon size={size} className={cls} />;
};

export default function Library() {
  const [files, setFiles] = useState(null);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("Forms");
  const [visibility, setVisibility] = useState("parents");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/library").then((r) => setFiles(r.data)).catch(() => setFiles([]));
  useEffect(() => { load(); }, []);

  const uploadOne = async (file) => {
    const { data: init } = await api.post("/library/upload/init", { name: file.name, content_type: file.type, size: file.size, category, visibility });
    const id = init.upload_id;
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("index", i);
      fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
      await api.post(`/library/upload/chunk/${id}`, fd);
      setProgress(Math.round(((i + 1) / total) * 100));
    }
    await api.post(`/library/upload/complete/${id}`, {});
  };

  const handleFiles = async (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setUploading(true);
    try {
      for (const f of arr) { setProgress(0); await uploadOne(f); }
      toast.success(`${arr.length} file${arr.length === 1 ? "" : "s"} uploaded`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); setProgress(0); if (inputRef.current) inputRef.current.value = ""; }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? Parents will no longer see it.`)) return;
    try { await api.delete(`/library/${f.id}`); setFiles((l) => l.filter((x) => x.id !== f.id)); toast.success("File deleted"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const shown = (files || []).filter((f) => filter === "all" || f.category === filter);

  return (
    <div className="animate-fade-up max-w-5xl">
      <PageHeader title="Document & Media Library" subtitle="Upload files, forms, photos and videos — share them with your club's families." testid="library-header" />

      <SectionCard title="Upload files" className="mb-5" testid="library-upload-card"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 w-36" data-testid="library-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={() => setVisibility((v) => (v === "parents" ? "staff" : "parents"))} data-testid="library-visibility-toggle"
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm transition-colors ${visibility === "parents" ? "border-secondary/40 bg-secondary/10 text-secondary" : "border-border bg-accent text-muted-foreground"}`}>
              {visibility === "parents" ? <><Users size={14} /> Visible to parents</> : <><EyeOff size={14} /> Staff only</>}
            </button>
          </div>
        }>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
          data-testid="library-dropzone">
          <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="library-file-input"
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.mp3,.wav" />
          <FolderOpen size={30} className="mx-auto text-muted-foreground mb-3" />
          {uploading ? (
            <div data-testid="library-upload-progress">
              <p className="text-sm font-medium">Uploading… {progress}%</p>
              <div className="h-1.5 bg-accent rounded-full mt-2 max-w-xs mx-auto overflow-hidden"><div className="h-full brand-gradient transition-all" style={{ width: `${progress}%` }} /></div>
            </div>
          ) : (
            <>
              <p className="text-sm font-medium">Tap to choose files or drag & drop here</p>
              <p className="text-xs text-muted-foreground mt-1">Images, videos, PDF, Word, Excel, CSV · up to 200MB each</p>
            </>
          )}
        </div>
      </SectionCard>

      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="library-filters">
        {["all", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`library-filter-${c}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors duration-150 ${filter === c ? "brand-gradient text-white" : "bg-accent text-muted-foreground hover:text-foreground"}`}>{c}</button>
        ))}
      </div>

      {files === null ? <Loader /> : shown.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No files yet" desc="Upload forms, policies, photos or match videos above — anything you want to share with your club." testid="library-empty" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
          {shown.map((f) => (
            <div key={f.id} className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" data-testid={`library-item-${f.id}`}>
              <div className="h-40 bg-accent/50 flex items-center justify-center overflow-hidden">
                {isImage(f.content_type) ? (
                  <img src={fileUrl(f)} alt={f.name} className="w-full h-full object-cover" loading="lazy" data-testid={`library-thumb-${f.id}`} />
                ) : isVideo(f.content_type) ? (
                  <video src={fileUrl(f)} controls preload="metadata" className="w-full h-full object-cover bg-black" data-testid={`library-video-${f.id}`} />
                ) : (
                  <TypeIcon ct={f.content_type} size={44} />
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start gap-2">
                  <TypeIcon ct={f.content_type} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate" title={f.name}>{f.name}</p>
                    <p className="text-xs text-muted-foreground">{f.category} · {humanSize(f.size || 0)} · {(f.created_at || "").slice(0, 10)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${f.visibility === "parents" ? "bg-secondary/15 text-secondary" : "bg-accent text-muted-foreground"}`} data-testid={`library-visibility-${f.id}`}>
                    {f.visibility === "parents" ? <><Eye size={10} /> Parents</> : <><EyeOff size={10} /> Staff</>}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                  <a href={fileUrl(f, true)} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid={`library-download-${f.id}`}><Download size={14} /> Download</a>
                  <button onClick={() => del(f)} className="ml-auto text-muted-foreground hover:text-destructive" data-testid={`library-delete-${f.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
